import { CHART_HEAD, CHART_UNDER_HEAD } from '@/components/charts/chart-header-layout'
import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'
import StatGridSkeleton from '@/screens/stock/components/overview/StatGridSkeleton'

/*
 * Stock · Overview before the company has loaded: the LOADED tab, block for
 * block — the quote's two lines over the chart, then one block per section,
 * each the loaded section's whole height at a desktop and a phone step (see
 * `SECTION_BLOCKS`): a block per row or cell read as noise. The heights are
 * measured off the loaded page (NABIL) at 1440 and 375, so they hold at the
 * two ends; a width between them can differ by a card's wrapped line, which
 * is the price of not drawing the cards' insides.
 *
 * The in-card loading state below (`KeyNumbersSkeleton`) keeps its bars: it
 * draws inside a real card that is already on screen. The greed meter and the
 * company's notices live in the rail only, so the body has no block for them.
 *
 * "Your position" is not drawn. It exists only for a member who holds the
 * stock, which most visitors do not, and a card that vanishes is worse than
 * one that arrives. A holder's page gains it when it lands.
 *
 * Bars only, no words. Its own light module: the Stock route's
 * `pendingComponent` draws it and ships in the entry chunk, so it imports
 * Skeleton-weight modules only (see `StatGridSkeleton`).
 */

/**
 * The grid's six cells, label over figure (the mock draws no caption). No footnote line: the
 * loaded card draws one only to say why its figures are dashes.
 */
export function KeyNumbersSkeleton() {
  return <StatGridSkeleton cells={6} columns="six" />
}

/**
 * Mitra's take while it loads: the speech bubble beside the pointing Mitra, sized to a ready take
 * (measured on the loaded NABIL page at 1440: a two-line headline, the summary, the link and the
 * bubble's 16px foot margin, 172px, on the 176 rung; a phone wraps to about 260).
 */
export const TAKE_CARD_BLOCK = 'h-44 @max-xl:h-64'

/**
 * The sections under the chart, one block each, measured on the loaded page at 1440 and snapped:
 * Mitra's take (`TAKE_CARD_BLOCK`), the 52-week range (96), key numbers in one row of six (head
 * and card, 120), price info, then the company.
 */
const SECTION_BLOCKS = [
  TAKE_CARD_BLOCK,
  'h-24 max-md:h-28',
  'h-30 max-md:h-60',
  'h-46 max-md:h-84',
  'h-48 max-md:h-76',
] as const

/** The quote's price (`h-2xl`, a 36px line) and its move (`h-s`, 20px), over the chart. */
function QuoteSkeleton() {
  return (
    <div>
      <div className="flex h-9 items-center">
        <Skeleton className="h-7 w-32 rounded-sm" />
      </div>
      <div className="flex h-5 items-center">
        <Skeleton className="h-3 w-28 rounded-xs" />
      </div>
    </div>
  )
}

export function OverviewSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col">
        {/* The loaded `ChartHeader`'s own boxes: the quote left; the toggle and chart button, the
            six periods under them, right (or under the quote, below its container step). */}
        <div className={CHART_HEAD.root}>
          <div className={CHART_HEAD.grid}>
            <div className={CHART_HEAD.info}>
              <QuoteSkeleton />
            </div>
            <div className={CHART_HEAD.actions}>
              <div className={cn(CHART_HEAD.controls, 'justify-between')}>
                <Skeleton className="h-10 w-36" />
                <Skeleton className="size-10" />
              </div>
              <Skeleton className="h-9 w-74" />
            </div>
          </div>
        </div>
        {/* The 212 chart, then the 12 to the fallback-reason line under it. */}
        <Skeleton className={cn(CHART_UNDER_HEAD, 'mb-3 h-53 w-full rounded-lg')} />
      </div>
      {SECTION_BLOCKS.map((height) => (
        <Skeleton key={height} className={cn('w-full rounded-lg', height)} />
      ))}
    </div>
  )
}
